<script lang="ts" setup>
const { isAuthenticated, login, user } = useAuth()
const { $toast } = useNuxtApp()

const route = useRoute()

const personId = computed(() => user.value?.['https://login.bcc.no/claims/personId'])

onBeforeMount(async () => {
  if (!isAuthenticated.value) {
    await login(route.fullPath)

    return
  }

  if (user.value) await ensureUserRow()
})

/**
 * Make sure the Auth0 session has a row in `users` behind it.
 *
 * Nothing in here may reject. It runs in `onBeforeMount`, so an escaping
 * rejection replaces the entire app with Nuxt's error screen - which is how a
 * paused database showed a room full of bidders a bare "500" instead of the
 * auction. The `createUser()` call used to escape from both branches.
 */
async function ensureUserRow() {
  try {
    if (await $fetch(`/api/users/${personId.value}`)) return
  }
  catch (error) {
    // Only a missing row is worth creating one for. Anything else - a 502 from
    // an unreachable database most of all - means the POST would just fail a
    // second time, and the second failure is the one that used to be fatal.
    if ((error as { statusCode?: number }).statusCode !== 404) {
      return reportFailure(error)
    }
  }

  try {
    await createUser()
  }
  catch (error) {
    reportFailure(error)
  }
}

/**
 * Say something, rather than nothing. The app stays up either way: the screens
 * that actually need the row await `useUser().waitForDbUser()`, which reports a
 * missing one in Norwegian on its own.
 */
function reportFailure(error: unknown) {
  console.error('[app] could not resolve the signed-in user:', error)

  $toast('Kunne ikke laste brukeren din. Prøv å laste siden på nytt.', {
    position: 'bottom-center',
  })
}

async function createUser() {
  if (!user.value) return

  return $fetch('/api/users', {
    method: 'post',
    body: {
      name: user.value.name,
      email: user.value.email,
      auth0Id: user.value.sub,
      personId: personId.value,
    },
  })
}
</script>

<template>
  <NuxtLoadingIndicator color="black" />
  <NuxtLayout>
    <NuxtPage />
    <Toaster />
  </NuxtLayout>
</template>
